import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Combobox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Option, Spinner, Text } from "@fluentui/react-components";
import { useAppContext } from "../../../AppContext";
import { useEffect, useState } from "react";
import { DismissRegular } from "@fluentui/react-icons";
import { User } from "../../../models/users";
import authService from "../../../services/authService";
import { getUserByEmail } from "../../../services/usersService";
import { getRoles } from "@testing-library/react";
import { createUserRoleAssignment, getRolesByType } from "../../../services/roleService";
import { RoleDTO } from "../../../models/role";
import { CreateTask } from "../../../models/task";
import { Module } from "../../../models/module";
import { createTenderManagerTask } from "../../../services/taskService";

type Props = {
  onClose: () => void;
  open: boolean;
  moduleID: number;
};

const TenderManagerDialog = (props: Props) => {
  const [user, setUser] = useState<User>();
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [userFetched, setUserFetched] = useState(false);
  const [fetchError, setFetchError] = useState<string>("");

  const {user: loggedUser} = useAppContext(); 

  useEffect(() => {
    if (props.open) {
      setIsAdmin(loggedUser?.roles.some((r: RoleDTO)=> r.roleType === "General"));
      setEmail("");
      setUser(undefined);
      setUserFetched(false);
      setFetchError("");
    }
  }, [props.open]);

  const handleFetchUser = async () => {
    if (!email) {
      setFetchError("Vänligen ange en e-postadress");
      return;
    }

    setIsFetching(true);
    setFetchError("");
    try {
      const userData = await getUserByEmail(email);
      setUser(userData);
      setUserFetched(true);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setFetchError("Ingen användare hittades med denna e-postadress");
      setUserFetched(false);
    } finally {
      setIsFetching(false);
    }
  };

  const handleFetchAnother = () => {
    setUser(undefined);
    setUserFetched(false);
    setFetchError("");
    setEmail("");
  };

  const handleSave = async () => {
    const roles = await getRolesByType("Supplier");

    const roleID = roles.find((r: any) => r.roleName === "Föreståndare")?.roleID;

    await createUserRoleAssignment(user!.userID, roleID, props.moduleID);

    console.log("Saving user:", user);

    props.onClose();
  };

  const createTask = async () => {
    const roles = await getRolesByType("Supplier");

    const roleID = roles.find((r: any) => r.roleName === "Föreståndare")?.roleID;

    const task: CreateTask = {
        taskName: `Föreståndare ${user?.email}`,
        taskDescription: `Uppgift att lägga till ${user?.email} som föreståndare för ${getModuleName(props.moduleID)}.`,
        taskData: {
            taskModuleID: props.moduleID,
            userID: user?.userID,
            roleID: roleID
        }
    };

    await createTenderManagerTask(task);

    props.onClose();    
  }

  const getModuleName = (moduleID: number): string => {
    return loggedUser.modules.find((m: Module) => m.moduleID == moduleID)?.moduleName || "Okänd avtal";
  }

  return(
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle            
          action={
            <Button
              appearance="subtle"
              shape="circular"
              aria-label="close"
              icon={<DismissRegular />}
              onClick={props.onClose}
            />
          }>Administrera föreståndare</DialogTitle>
          <DialogContent>
            <Field 
              label="E-post:" 
              validationMessage={fetchError}
              validationState={fetchError ? "error" : undefined}
            >
              <Input 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={userFetched}
              />
            </Field>
            {!userFetched && (
              <Button 
                onClick={handleFetchUser} 
                disabled={isFetching}
                appearance="primary"
                style={{ marginTop: '8px' }}
              >
                {isFetching ? "Hämtar användare..." : "Hämta användare"}
              </Button>
            )}
            {userFetched && user && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <Text weight="semibold" block style={{ marginBottom: '8px' }}>Användarinformation:</Text>
                <Text block style={{ marginBottom: '4px' }}><strong>Förnamn:</strong> {user.firstName || 'Ej angivet'}</Text>
                <Text block style={{ marginBottom: '4px' }}><strong>Efternamn:</strong> {user.lastName || 'Ej angivet'}</Text>
                <Text block style={{ marginBottom: '4px' }}><strong>Telefon:</strong> {user.phoneNumber || 'Ej angivet'}</Text>
                <Button 
                  onClick={handleFetchAnother}
                  style={{ marginTop: '12px' }}
                >
                  Hämta annan användare
                </Button>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Avbryt</Button>
            <Button appearance="primary" onClick={isAdmin ? handleSave : createTask} disabled={!userFetched}>{isAdmin ? "Spara" : "Begära ändringen"}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

export default TenderManagerDialog;